/**
 * client/src/utils/rum-beacon.ts — Public RUM page-load timing collector.
 *
 * Fixes the critical instrumentation gap: the previous pipeline POSTed page
 * load metrics to /api/admin/metrics/client which requires authentication.
 * Unauthenticated users on /auth/login received 401 and their data was silently
 * discarded — the most important user journey was completely unobservable.
 *
 * This module:
 *   1. Reads PerformanceNavigationTiming at module load (synchronous, zero cost)
 *   2. Exposes named recorder functions called from checkSession() and
 *      dismissHTMLPreloader() via window.__nashidRum
 *   3. Sends a complete structured beacon to POST /api/rum/public (no auth)
 *      exactly once, via navigator.sendBeacon, when the splash is dismissed
 *      or after a 30s safety-net timeout (whichever comes first)
 *
 * All timing values are integers (ms) relative to navigationStart.
 * The module is entirely fail-safe: every operation is wrapped in try/catch.
 */

export interface RumPageLoadPayload {
  sessionId: string;
  page: string;
  navigationStartTs: string;
  domContentLoadedMs: number | null;
  loadEventEndMs: number | null;
  firstReactRenderMs: number | null;
  authMeDispatchMs: number | null;
  authMeResponseMs: number | null;
  splashDismissedMs: number | null;
  fcpMs: number | null;
  lcpMs: number | null;
  ttfbMs: number | null;
  connectionType: string | null;
}

function generateSessionId(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 24);
  } catch {
    return Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
  }
}

function safeInt(v: number | undefined | null): number | null {
  if (v == null || !Number.isFinite(v) || v < 0) return null;
  return Math.round(Math.min(v, 300_000));
}

const _state: RumPageLoadPayload = {
  sessionId: generateSessionId(),
  page: typeof window !== "undefined" ? window.location.pathname : "/",
  navigationStartTs: new Date().toISOString(),
  domContentLoadedMs: null,
  loadEventEndMs: null,
  firstReactRenderMs: null,
  authMeDispatchMs: null,
  authMeResponseMs: null,
  splashDismissedMs: null,
  fcpMs: null,
  lcpMs: null,
  ttfbMs: null,
  connectionType: null,
};

let _flushed = false;
let _safetyTimer: ReturnType<typeof setTimeout> | null = null;

function readNavTiming(): void {
  try {
    const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const nav = entries[0];
    if (!nav) return;

    _state.navigationStartTs = new Date(Date.now() - performance.now()).toISOString();
    _state.domContentLoadedMs = safeInt(nav.domContentLoadedEventEnd - nav.startTime);

    if (nav.loadEventEnd > 0) {
      _state.loadEventEndMs = safeInt(nav.loadEventEnd - nav.startTime);
    }

    _state.ttfbMs = safeInt(nav.responseStart - nav.requestStart);
  } catch { /* ignore */ }
}

function readConnectionType(): void {
  try {
    const conn = (navigator as any).connection;
    if (conn?.effectiveType) {
      _state.connectionType = String(conn.effectiveType).slice(0, 16);
    }
  } catch { /* ignore */ }
}

function observeFcp(): void {
  try {
    if (typeof PerformanceObserver === "undefined") return;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          _state.fcpMs = safeInt(entry.startTime);
          observer.disconnect();
        }
      }
    });
    observer.observe({ type: "paint", buffered: true });
  } catch { /* ignore */ }
}

function observeLcp(): void {
  try {
    if (typeof PerformanceObserver === "undefined") return;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) {
        _state.lcpMs = safeInt(last.startTime);
      }
    });
    observer.observe({ type: "largest-contentful-paint", buffered: true });
  } catch { /* ignore */ }
}

function flush(): void {
  if (_flushed) return;
  _flushed = true;

  if (_safetyTimer !== null) {
    clearTimeout(_safetyTimer);
    _safetyTimer = null;
  }

  try {
    readNavTiming();
    readConnectionType();

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        "/api/rum/public",
        new Blob([JSON.stringify(_state)], { type: "application/json" }),
      );
    } else {
      fetch("/api/rum/public", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(_state),
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* never let telemetry errors affect the user */ }
}

export function recordFirstReactRender(): void {
  try {
    _state.firstReactRenderMs = safeInt(performance.now());
  } catch { /* ignore */ }
}

export function recordAuthMeDispatch(): void {
  try {
    _state.authMeDispatchMs = safeInt(performance.now());
  } catch { /* ignore */ }
}

export function recordAuthMeResponse(): void {
  try {
    _state.authMeResponseMs = safeInt(performance.now());
  } catch { /* ignore */ }
}

export function recordSplashDismissed(): void {
  try {
    _state.splashDismissedMs = safeInt(performance.now());
    _state.page = window.location.pathname;
    flush();
  } catch { /* ignore */ }
}

export function getRumSessionId(): string {
  return _state.sessionId;
}

export function initRumBeacon(): void {
  try {
    readNavTiming();
    readConnectionType();
    observeFcp();
    observeLcp();

    _safetyTimer = setTimeout(() => {
      flush();
    }, 30_000);

    if (document.readyState === "complete") {
      readNavTiming();
    } else {
      window.addEventListener("load", () => {
        setTimeout(readNavTiming, 0);
      }, { once: true });
    }

    (window as any).__nashidRum = {
      recordFirstReactRender,
      recordAuthMeDispatch,
      recordAuthMeResponse,
      recordSplashDismissed,
      getSessionId: getRumSessionId,
    };
  } catch { /* ignore */ }
}
